import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import useValueLabels from '../hooks/useValueLabels';
import { Plus, FileText, Download, Trash2, Eye } from 'lucide-react';
import axios from 'axios';
import FileUpload from './FileUpload';
import usePermissions from '../hooks/usePermissions';
import { Switch } from './ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { FileVisibilityBadge, RoleVisibilityPicker, UNRESTRICTED_UPLOADER_ROLES, isAdminUser, visibilityFromDefault, BulkVisibilityButton, RoleFilter, matchesRoleFilter } from './FileVisibility';
import { Checkbox } from './ui/checkbox';
import { fmtDateTimeWeek } from '../lib/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined categories shared across employees / customers / projects.
// Keeps captions in printed reports consistent ("[Passport]", "[Contract]" …).
// Editable in Settings → General; this is the fallback list.
const DEFAULT_ATTACHMENT_CATEGORIES = [
  'Contract',
  'Passport',
  'ID Card',
  'Driving Licence',
  'Visa',
  'Work Permit',
  'Certificate',
  'Photo',
  'Drawing',
  'Invoice',
  'Report',
  'Other',
];

const AttachmentManager = ({ entityType = 'employee', entityId, entityName, employeeId, canDelete = true }) => {
  const { t } = useTranslation();
  // Documents are private by default. Whoever may edit documents sees the
  // share toggle and can flip it on the spot — crew only ever see files
  // that have been shared with them.
  const { can } = usePermissions();
  const canShare = can('project_documents.edit');
  const { user: me } = useAuth();
  // Users/Employees can't restrict: their uploads are always visible to everyone.
  const canPickVisibility = !!me && !UNRESTRICTED_UPLOADER_ROLES.includes(String(me.role || '').toLowerCase());
  const [visibility, setVisibility] = useState({ all: true, roles: [] });
  const isAdmin = isAdminUser(me);
  const [catDefaults, setCatDefaults] = useState({});
  const [selected, setSelected] = useState([]);
  const [roleFilter, setRoleFilter] = useState({ on: false, roles: [] });
  // Use employeeId if provided (for MyProfile), otherwise use entityId
  const actualEntityId = employeeId || entityId;
  const actualEntityType = employeeId ? 'employee' : entityType;
  
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [description, setDescription] = useState('');
  const vl = useValueLabels();
  const [category, setCategory] = useState('');
  // The document-type list lives in Settings so the app and the public
  // application form always offer the same choices.
  const [categories, setCategories] = useState(DEFAULT_ATTACHMENT_CATEGORIES);

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(r => {
        setCategories(r.data?.attachment_categories?.length ? r.data.attachment_categories : DEFAULT_ATTACHMENT_CATEGORIES);
        setCatDefaults(r.data?.attachment_category_visibility || {});
      })
      .catch(() => { /* keep the fallback list */ });
  }, []);

  useEffect(() => {
    if (actualEntityId) {
      fetchAttachments();
    }
  }, [actualEntityId, actualEntityType]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/attachments/${actualEntityType}/${actualEntityId}`);
      setAttachments(response.data);
      setSelected([]);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      toast.error(t('common.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = (uploadResponse) => {
    setUploadDialogOpen(false);
    setDescription('');
    setCategory('');
    setVisibility({ all: true, roles: [] });
    fetchAttachments(); // Refresh the list
    toast.success(t('attachments.toasts.uploaded'));
  };

  const handleUploadError = (error) => {
    console.error('Upload error:', error);
  };

  const downloadAttachment = async (attachment) => {
    if (attachment.missing_file || attachment.available === false) {
      toast.error(
        `"${attachment.original_filename}" is no longer available. ` +
        'It was lost during a server restart. Please delete this entry and re-upload.'
      );
      return;
    }
    try {
      const response = await axios.get(`${API}/files/attachment/${attachment.id}`, {
        responseType: 'blob',
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      // 410 GONE → file was lost (legacy filesystem record)
      if (error.response?.status === 410) {
        // Refresh so the row gets the "Missing" badge straight away
        fetchAttachments();
        toast.error(
          error.response?.data?.detail ||
          `"${attachment.original_filename}" is no longer available. Please re-upload.`,
          { duration: 6000 }
        );
        return;
      }
      const detail = error.response?.data?.detail;
      toast.error(detail || t('common.errorGeneric'));
    }
  };

  const deleteAttachment = async (attachmentId) => {
    try {
      await axios.delete(`${API}/attachment/${attachmentId}`);
      toast.success(t('attachments.toasts.deleted'));
      fetchAttachments(); // Refresh the list
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('attachments.toasts.deleteError'));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return fmtDateTimeWeek(dateString);
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType.startsWith('image/')) {
      return '🖼️';
    } else if (contentType === 'application/pdf') {
      return '📄';
    } else if (contentType.includes('word') || contentType.includes('document')) {
      return '📝';
    } else if (contentType.includes('sheet') || contentType.includes('excel')) {
      return '📊';
    } else {
      return '📎';
    }
  };

  const updateVisibility = async (attachmentId, v) => {
    try {
      const r = await axios.patch(`${API}/attachment/${attachmentId}`, { visible_to_all: v.all, visible_roles: v.all ? [] : v.roles });
      setAttachments(prev => prev.map(a => a.id === attachmentId ? { ...a, visible_to_all: r.data.visible_to_all, visible_roles: r.data.visible_roles } : a));
      toast.success(t('fileVisibility.saved'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('fileVisibility.saveFailed'));
    }
  };

  const updateCrewVisible = async (attachmentId, value) => {
    try {
      await axios.patch(`${API}/attachment/${attachmentId}`, { crew_visible: value });
      setAttachments(prev => prev.map(a => a.id === attachmentId ? { ...a, crew_visible: value } : a));
      toast.success(value ? t('auto.misc.sharedWithCrew') : t('auto.misc.hiddenFromCrew'));
    } catch {
      toast.error(t('auto.misc.couldNotChangeSharing'));
    }
  };

  const updateAttachmentCategory = async (attachmentId, newCategory) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/attachment/${attachmentId}`, {
        category: newCategory || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAttachments(prev => prev.map(a => a.id === attachmentId ? { ...a, category: newCategory || null } : a));
    } catch (error) {
      toast.error(t('auto.misc.failedToUpdateCategory'));
    }
  };

  const shown = attachments.filter((a) => matchesRoleFilter(a, roleFilter));

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('attachments.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>{t('attachments.title')} ({attachments.length})</CardTitle>
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                {t('auto.misc.addAttachment')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{t('attachments.upload')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('auto.misc.category')}
                  </label>
                  <Select value={category} onValueChange={(v) => { setCategory(v); setVisibility(visibilityFromDefault(catDefaults, v)); }}>
                    <SelectTrigger data-testid="attachment-upload-category-select">
                      <SelectValue placeholder={t('auto.misc.whatIsThisFileOptional')} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(c => (
                        <SelectItem key={c} value={c} data-testid={`attachment-category-${c.replace(/\s+/g,'-').toLowerCase()}`}>{vl(c)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('auto.misc.descriptionOptional')}
                  </label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('auto.misc.enterDescriptionForThisFile')}
                  />
                </div>
                {canPickVisibility && (
                  <RoleVisibilityPicker value={visibility} onChange={setVisibility} testId="attachment-upload-visibility" />
                )}
                <FileUpload
                  type="attachment"
                  entityType={actualEntityType}
                  entityId={actualEntityId}
                  description={description}
                  category={category}
                  visibility={canPickVisibility ? visibility : null}
                  onUploadComplete={handleUploadComplete}
                  onUploadError={handleUploadError}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif,.webp"
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {attachments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>{t('auto.misc.noAttachmentsFound')}</p>
            <p className="text-sm">{t('auto.misc.uploadFilesToGetStarted')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2" data-testid="attachment-admin-toolbar">
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox checked={shown.length > 0 && selected.length === shown.length}
                    onCheckedChange={(on) => setSelected(on ? shown.map((a) => a.id) : [])} data-testid="attachment-select-all" />
                  {t('fileVisibility.selectAllRows')}
                </label>
                <BulkVisibilityButton fileIds={selected} legacyCount={attachments.filter((a) => selected.includes(a.id) && a.visible_to_all === undefined).length} onDone={fetchAttachments} testId="attachment-bulk-visibility" />
                <RoleFilter value={roleFilter} onChange={(f) => { setRoleFilter(f); setSelected([]); }} testId="attachment-role-filter" />
                {roleFilter.on && (
                  <span className="text-xs text-gray-500" data-testid="attachment-filter-count">
                    {t('fileVisibility.shownOf', { shown: shown.length, total: attachments.length })}
                  </span>
                )}
              </div>
            )}
            {shown.map((attachment) => {
              const isMissing = attachment.missing_file || attachment.available === false;
              return (
              <div
                key={attachment.id}
                className={`flex items-center justify-between p-3 border rounded-lg ${isMissing ? 'bg-red-50 border-red-200' : 'hover:bg-gray-50'}`}
                data-testid={`attachment-row-${attachment.id}`}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {isAdmin && (
                    <Checkbox checked={selected.includes(attachment.id)}
                      onCheckedChange={(on) => setSelected((p) => on ? [...p, attachment.id] : p.filter((x) => x !== attachment.id))}
                      data-testid={`attachment-select-${attachment.id}`} />
                  )}
                  <span className="text-2xl">
                    {getFileIcon(attachment.content_type)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate flex items-center gap-2">
                      <span className={isMissing ? 'line-through text-gray-500' : ''}>
                        {attachment.original_filename}
                      </span>
                      {isMissing && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 border border-red-300"
                          data-testid={`attachment-missing-badge-${attachment.id}`}
                          title={t('auto.misc.thisFileWasLostDuringA')}
                        >
                          Missing — re-upload
                        </span>
                      )}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 mt-1">
                      <Select
                        value={attachment.category || ''}
                        onValueChange={(v) => updateAttachmentCategory(attachment.id, v === '__none__' ? '' : v)}
                      >
                        <SelectTrigger
                          className="h-6 px-2 text-[11px] w-auto min-w-[110px] bg-red-50 border-red-200 text-red-800 font-semibold"
                          data-testid={`attachment-category-trigger-${attachment.id}`}
                        >
                          <SelectValue placeholder={t('auto.misc.setCategory')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t('auto.misc.none')}</SelectItem>
                          {categories.map(c => (
                            <SelectItem key={c} value={c}>{vl(c)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {attachment.visible_to_all === undefined && canShare ? (
                        <span className="inline-flex items-center gap-1">
                          <Switch
                            checked={!!attachment.crew_visible}
                            onCheckedChange={(v) => updateCrewVisible(attachment.id, v)}
                            data-testid={`attachment-crew-toggle-${attachment.id}`}
                          />
                          <span className={attachment.crew_visible ? 'text-emerald-700' : 'text-gray-500'}>
                            {attachment.crew_visible ? t('auto.misc.sharedWithCrew') : t('auto.misc.private')}
                          </span>
                        </span>
                      ) : attachment.visible_to_all !== undefined ? (
                        <FileVisibilityBadge attachment={attachment} onSave={updateVisibility} />
                      ) : null}
                      <span>{formatFileSize(attachment.file_size)}</span>
                      <span>•</span>
                      <span>{formatDate(attachment.uploaded_at)}</span>
                    </div>
                    {attachment.description && (
                      <p className="text-xs text-gray-600 mt-1">
                        {attachment.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadAttachment(attachment)}
                    disabled={isMissing}
                    title={isMissing ? t('auto.misc.fileIsNoLongerAvailable') : t('auto.misc.download')}
                    data-testid={`attachment-download-${attachment.id}`}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {canDelete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('auto.misc.deleteAttachment')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete &quot;{attachment.original_filename}&quot;? 
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('auto.misc.cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteAttachment(attachment.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            {t('auto.misc.delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AttachmentManager;